/**
 * 最小限のZIP読み出し。
 *
 * LINEなどのメッセージアプリはIGCファイル（拡張子 .igc）をそのまま送れず、
 * 送り手は圧縮してzipにしてから送ることになる。受け手（多くはスマホ）は
 * 送られてきたzipをその場で展開する手段が無いことが多いため、アプリ側で
 * zipのまま受け取れるようにする。
 *
 * 対応する圧縮方式は「無圧縮」と「deflate」（ZIPで実質的に標準の方式）のみ。
 * deflateの展開はブラウザ・Node（18以降）の両方に入っている
 * Compression Streams API（`DecompressionStream`）を使う。専用ライブラリを
 * 増やさずに済む（`test/helpers/zip.ts` はテスト専用の別実装で、
 * Node標準の`node:zlib`を使うNode限定版。同じ理由・同じ最小実装だが、
 * こちらはブラウザでも動く必要があるため独立させてある）。
 *
 * 中央ディレクトリから読むこと: ストリーミングで作られたzipはローカル
 * ヘッダのサイズ欄が0のままのことがある（実サイズはデータ後方の
 * data descriptorにある）ため、ローカルヘッダのサイズを信じてはいけない。
 */

const SIG_LOCAL_FILE = 0x04034b50; // ローカルファイルヘッダ
const SIG_EOCD = 0x06054b50; // 中央ディレクトリ終端
const SIG_CENTRAL = 0x02014b50; // 中央ディレクトリのエントリ

interface ZipDirEntry {
  name: string;
  method: number;
  compressedSize: number;
  localHeaderOffset: number;
}

/** 先頭バイトがZIPのローカルファイルヘッダ署名と一致するか（ファイル種別の判定に使う） */
export function looksLikeZip(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return view.getUint32(0, true) === SIG_LOCAL_FILE;
}

/** 中央ディレクトリを読んでエントリ一覧を返す */
function readCentralDirectory(bytes: Uint8Array): ZipDirEntry[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // EOCD は末尾にあり、可変長コメントが続きうるので後ろから探す
  let eocd = -1;
  for (let i = bytes.length - 22; i >= 0 && i > bytes.length - 22 - 65536; i--) {
    if (view.getUint32(i, true) === SIG_EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('ZIPの中央ディレクトリ終端が見つかりません（壊れているか、ZIPではありません）');

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const decoder = new TextDecoder('latin1');

  const entries: ZipDirEntry[] = [];
  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== SIG_CENTRAL) break;
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    entries.push({
      name: decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength)),
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      localHeaderOffset: view.getUint32(offset + 42, true),
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

/** ZIP内のファイル名の一覧 */
export function listZipEntryNames(bytes: Uint8Array): string[] {
  return readCentralDirectory(bytes).map((e) => e.name);
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  // Blob() は ArrayBuffer 裏付けの TypedArray しか受け付けないため、
  // subarray() 由来の view をそのまま渡せるとは限らない（型上は
  // ArrayBufferLike）。コピーして明確に ArrayBuffer 裏付けにする
  const stream = new Blob([new Uint8Array(data)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** ZIP内の1エントリを取り出す（省略時は先頭のエントリ） */
export async function readZipEntryBytes(bytes: Uint8Array, entryName?: string): Promise<Uint8Array> {
  const entries = readCentralDirectory(bytes);
  const entry = entryName === undefined ? entries[0] : entries.find((e) => e.name === entryName);
  if (!entry) {
    throw new Error(
      `ZIPにエントリが見つかりません: ${entryName ?? '(先頭)'}（収録: ${entries.map((e) => e.name).join(', ') || 'なし'}）`,
    );
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const lh = entry.localHeaderOffset;
  const nameLength = view.getUint16(lh + 26, true);
  const extraLength = view.getUint16(lh + 28, true);
  const start = lh + 30 + nameLength + extraLength;
  const data = bytes.subarray(start, start + entry.compressedSize);

  if (entry.method === 0) return data;
  if (entry.method === 8) return inflateRaw(data);
  throw new Error(`未対応の圧縮方式です: ${entry.method}（${entry.name}）`);
}

/**
 * ZIP内から最初に見つかった .igc エントリをテキストとして取り出す。
 * LINEなどで送られてきたzipを、そのまま読み込めるようにするための入口。
 *
 * ★ IGCは純ASCIIなので`latin1`でデコードしている。0x80以上を含む可能性が
 *   あるファイル（Shift-JISのコメントを含むmap.txt等）には使わないこと
 *   ——WHATWGの仕様上`latin1`ラベルは実質windows-1252の別名で、
 *   0x80-0x9Fの範囲が真のISO-8859-1（バイト値=コードポイント）とは
 *   一致しない（`zip.test.ts`のコメント参照）。
 */
export async function extractIgcTextFromZip(bytes: Uint8Array): Promise<string> {
  const entries = readCentralDirectory(bytes);
  const igcEntry = entries.find((e) => /\.igc$/i.test(e.name));
  if (!igcEntry) {
    throw new Error(`ZIP内にIGCファイルが見つかりません（収録: ${entries.map((e) => e.name).join(', ') || 'なし'}）`);
  }
  const data = await readZipEntryBytes(bytes, igcEntry.name);
  return new TextDecoder('latin1').decode(data);
}

/**
 * ZIP内から最初に見つかった .plt エントリをテキストとして取り出す。
 * TV（Taskview）が配るXDD等のスケッチが、この形式で送られてくることがある。
 * IGCと同じ理由・同じ最小実装（`extractIgcTextFromZip`参照）。
 */
export async function extractOziPltTextFromZip(bytes: Uint8Array): Promise<string> {
  const entries = readCentralDirectory(bytes);
  const pltEntry = entries.find((e) => /\.plt$/i.test(e.name));
  if (!pltEntry) {
    throw new Error(`ZIP内に.pltファイルが見つかりません（収録: ${entries.map((e) => e.name).join(', ') || 'なし'}）`);
  }
  const data = await readZipEntryBytes(bytes, pltEntry.name);
  return new TextDecoder('latin1').decode(data);
}
